/**
 * Async generator that yields `void` at a fixed interval, respecting an
 * `AbortSignal` for clean cancellation.
 *
 * Behaves like `setInterval` but as an `AsyncIterable`, which makes it
 * composable with `for await` loops and backpressure-aware: the next tick
 * only starts after the consumer finishes processing the previous one.
 *
 * Abort listeners are cleaned up on both paths (timer fire and abort fire)
 * to avoid accumulating listeners on long-lived consumers.
 *
 * @example
 * ```ts
 * const signal = abortController.signal;
 *
 * for await (const _ of intervalTicker(signal, 10_000)) {
 *   await reconcileMissedFills();
 * }
 * ```
 */
export async function* intervalTicker(signal: AbortSignal, intervalMs: number): AsyncGenerator<void, void, void> {
  while (!signal.aborted) {
    await new Promise<void>((resolve) => {
      if (signal.aborted) {
        resolve();
        return;
      }
      // Remove the abort listener on BOTH paths (timer fire + abort fire)
      // to avoid accumulating listeners on every tick for long-lived consumers.
      const onAbort = (): void => {
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(() => {
        signal.removeEventListener("abort", onAbort);
        resolve();
      }, intervalMs);
      signal.addEventListener("abort", onAbort, { once: true });
    });
    if (signal.aborted) return;
    yield;
  }
}
